import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import DataTable, { Column } from './DataTable';
import { Button } from './ui';
import Modal from './Modal';
import MultiSelect, { SelectId } from './MultiSelect';
import { seasonLabel } from '../utils/seasonLabel';
import '../App.css';
import { translateError } from '../utils/errorMessages';

interface IScheduleTemplate {
  _id: string | number;
  name: string;
  startTime: string;
  endTime: string;
  recurringPattern: string;
  firstOccurrence?: string | null;
  endsOn?: string | null;
  isActive: boolean;
}

interface IScheduleEvent {
  _id: string | number;
  name: string;
  startTime: string;
  endTime: string;
}

interface ICourseSchedule {
  templates: IScheduleTemplate[];
  events: IScheduleEvent[];
}

interface ITemplateOption {
  _id: number;
  name: string;
  courseId?: number | null;
}

interface CourseScheduleWidgetProps {
  courseId: number;
  /** Course term — rendered as a read-only season header above the schedule. */
  termStart?: string | null;
  termEnd?: string | null;
  /** Bump to force a refetch without changing courseId (e.g. after renewal). */
  refreshKey?: number;
}

const CourseScheduleWidget: React.FC<CourseScheduleWidgetProps> = ({ courseId, termStart, termEnd, refreshKey }) => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();

  const [schedule, setSchedule] = useState<ICourseSchedule>({ templates: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | number | null>(null);

  // Attach-template modal state
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [allTemplates, setAllTemplates] = useState<ITemplateOption[]>([]);
  const [attachIds, setAttachIds] = useState<SelectId[]>([]);
  const [attachLoading, setAttachLoading] = useState(false);

  const canManage = hasPermission('activities:write');

  const fetchSchedule = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.courses.getSchedule(courseId);
      const d = data as ICourseSchedule;
      setSchedule({ templates: d.templates || [], events: d.events || [] });
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchSchedule();
    // refreshKey bumps re-run the fetch even though fetchSchedule (keyed on
    // courseId) is unchanged after a same-id renewal.
  }, [fetchSchedule, refreshKey]);

  const handleDetachTemplate = async (templateId: string | number) => {
    if (!canManage) return;
    setProcessingId(templateId);
    try {
      await api.courses.detachTemplate(courseId, Number(templateId));
      addToast(t('courseSchedule.detached'), 'success');
      fetchSchedule();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDetachEvent = async (eventId: string | number) => {
    if (!canManage) return;
    setProcessingId(eventId);
    try {
      await api.courses.detachEvent(courseId, Number(eventId));
      addToast(t('courseSchedule.detached'), 'success');
      fetchSchedule();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const openAttachModal = async () => {
    setAttachIds([]);
    setShowAttachModal(true);
    try {
      const { data } = await api.eventTemplates.list();
      setAllTemplates(
        (data as ITemplateOption[]).map((tpl) => ({
          _id: Number(tpl._id),
          name: tpl.name,
          courseId: tpl.courseId ?? null,
        })),
      );
    } catch {
      // Non-critical — modal still opens with an empty list.
    }
  };

  const handleAttachTemplates = async () => {
    if (attachIds.length === 0) return;
    setAttachLoading(true);
    try {
      await api.courses.attachTemplates(courseId, attachIds.map(Number));
      addToast(t('courseSchedule.attached'), 'success');
      setShowAttachModal(false);
      setAttachIds([]);
      fetchSchedule();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setAttachLoading(false);
    }
  };

  const formatDate = (dateString?: string | null) =>
    dateString ? new Date(dateString).toLocaleDateString() : '-';

  const formatDateTime = (dateString?: string) =>
    dateString ? new Date(dateString).toLocaleString() : '-';

  const recurrenceLabel = (pattern: string): string => {
    const key = `templates.${pattern}`;
    const translated = t(key);
    // Fall back to the raw pattern if no translation exists for it.
    return translated === key ? pattern : translated;
  };

  // Templates already linked to this course should not appear in the picker.
  const templateOptions = useMemo(
    () => allTemplates.filter((tpl) => tpl.courseId !== courseId).map((tpl) => ({ _id: tpl._id, name: tpl.name })),
    [allTemplates, courseId],
  );

  const templateColumns: Column<IScheduleTemplate>[] = useMemo(() => {
    const cols: Column<IScheduleTemplate>[] = [
      {
        header: t('common.name'),
        render: (tpl) => tpl.name,
      },
      {
        header: t('courseSchedule.recurrence'),
        render: (tpl) => recurrenceLabel(tpl.recurringPattern),
      },
      {
        header: t('templates.firstOccurrence'),
        render: (tpl) => `${formatDate(tpl.firstOccurrence)} ${tpl.startTime}–${tpl.endTime}`,
      },
      {
        header: t('courseSchedule.endsOn'),
        render: (tpl) => (tpl.endsOn ? formatDate(tpl.endsOn) : '-'),
      },
      {
        header: t('courseSchedule.active'),
        render: (tpl) => (tpl.isActive ? t('common.yes') : t('common.no')),
      },
    ];
    if (canManage) {
      cols.push({
        header: t('common.actions'),
        render: (tpl) => (
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDetachTemplate(tpl._id)}
            disabled={processingId === tpl._id}
          >
            {t('courseSchedule.detach')}
          </Button>
        ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, canManage, processingId]);

  const eventColumns: Column<IScheduleEvent>[] = useMemo(() => {
    const cols: Column<IScheduleEvent>[] = [
      {
        header: t('common.name'),
        render: (ev) => ev.name,
      },
      {
        header: t('events.startTime'),
        render: (ev) => formatDateTime(ev.startTime),
      },
      {
        header: t('events.endTime'),
        render: (ev) => formatDateTime(ev.endTime),
      },
    ];
    if (canManage) {
      cols.push({
        header: t('common.actions'),
        render: (ev) => (
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDetachEvent(ev._id)}
            disabled={processingId === ev._id}
          >
            {t('courseSchedule.detach')}
          </Button>
        ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, canManage, processingId]);

  return (
    <div>
      <div style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>
        <strong>{t('courseSchedule.season')}:</strong> {seasonLabel(termStart, termEnd, t, i18n.language)}
      </div>

      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <>
          {/* Recurring sessions */}
          <div className="form-section" style={{ marginBottom: '1.5rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
                flexWrap: 'wrap',
                gap: '0.75rem',
              }}
            >
              <h4 className="form-section__title" style={{ margin: 0 }}>{t('courseSchedule.templates')}</h4>
              {canManage && (
                <Button variant="primary" size="sm" onClick={openAttachModal}>
                  {t('courseSchedule.attachTemplate')}
                </Button>
              )}
            </div>
            {schedule.templates.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>{t('courseSchedule.noTemplates')}</p>
            ) : (
              <DataTable data={schedule.templates} columns={templateColumns} />
            )}
          </div>

          {/* One-off events */}
          <div className="form-section">
            <h4 className="form-section__title">{t('courseSchedule.events')}</h4>
            {schedule.events.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>{t('courseSchedule.noEvents')}</p>
            ) : (
              <DataTable data={schedule.events} columns={eventColumns} />
            )}
          </div>
        </>
      )}

      <Modal
        isOpen={showAttachModal}
        onClose={() => { setShowAttachModal(false); setAttachIds([]); }}
        title={t('courseSchedule.attachTemplateTitle')}
        size="medium"
      >
        <div style={{ padding: '0.5rem 0' }}>
          <MultiSelect
            options={templateOptions}
            selectedIds={attachIds}
            onChange={setAttachIds}
            placeholder={t('courseSchedule.attachTemplate')}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
            <Button
              variant="secondary"
              onClick={() => { setShowAttachModal(false); setAttachIds([]); }}
              disabled={attachLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleAttachTemplates}
              loading={attachLoading}
              disabled={attachIds.length === 0}
            >
              {t('courseSchedule.attachTemplate')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CourseScheduleWidget;
